import React, { ReactElement, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Modal,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import TherapyMethodPickerDialog from "./TherapyMethodPickerDialog";
import {
  AddressDto,
  AddressDtoTypeEnum,
  AppointmentDto,
  AppStatsDto,
  NoteDto,
  RxSeriesDto,
  TherapyDetailsDto,
  TherapyDto,
  TherapyRxDto,
  TicketDto,
  TimeSlotDto,
  UpdateAppointmentDto,
} from "../../../api-client";
import Notes from "../Notes/Notes";
import { FlexListContainer, LabelItem, ValueItem } from "../../../molecules/FlexList";
import { mobiliTheme } from "../../../themes/mobiliTheme";

import { observer } from "mobx-react";
import { TicketsStoreContext } from "../../../stores/Ticketing/tickets.provider";
import useStore from "../../../helpers/useStore";
import { NotesContext } from "../../../stores/Notes/notes.provider";
import { UserContext } from "../../../stores/User/User.provider";
import { useCurrentLocation } from "../../../stores/User/user.queries";
import Controls from "../../../atoms/Controls";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { AppointmentContext } from "../../../stores/Appointment/appointment.provider";
import { useMasterTables, getTherapyId } from "../../../stores/MasterTables";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import FlatCard from "../../../atoms/FlatCard";
import { TherapyExt } from "../../../molecules/Calendar/Calendar.type";
import moment from "moment";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { visitTypeToIcon } from "../../../helpers/visitTypeMapping";
import AppointmentAddress from "./AppointmentAddress";
import { Delete, MoveDown } from "@mui/icons-material";
import notificationStore from "../../../stores/Notification/notificationStore";
import { TerminViewConstants } from "../../PatientOverview/TerminView";
import { RxStoreContext } from "../../../stores/Rx/rx.provider";
import WarningDialog from "../../../atoms/WarningDialog";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { de } from "date-fns/locale";
import {
  useDeleteHardTimeslot,
  useUpdateAppointment,
  useUpdateMultipleAppointments,
} from "../../../stores/Appointment/appointment.mutation";
import { useTickets } from "../../../stores/Ticketing";
import TicketsTable from "../../Ticketing/TicketsTable";
import { usePatient } from "../../../stores/Patients/patient.queries";
import { slotTherapy } from "../../../helpers/slotTherapy";
import { therapyRxTermLabel } from "../../../helpers/therapyRxLabel";

interface AppointmentEditCardProps {
  open: boolean;
  onClose: (actionPerformed: boolean) => void;
  appointment: AppointmentDto;
  patientId: number;
  isContinous?: boolean;
  isView?: TerminViewConstants;
}

interface AddressOption {
  label: string;
  value: string;
  id: string;
  visitLocation: string;
  icon: ReactElement;
}

const formatAddress = (address: AddressDto): string => {
  const { addressLine1, addressLine2, city, zip } = address.contactInfo || {};

  // Create an array of the parts of the address, filtering out empty or undefined values
  const addressParts = [addressLine1, addressLine2, city, zip].filter(
    (part) => part && part.trim()
  );

  // Join the parts with a comma and a space, ensuring no trailing comma
  return addressParts.join(", ");
};

const AppointmentEditCard: React.FC<AppointmentEditCardProps> = observer(
  ({ open, onClose, appointment, patientId, isContinous, isView }) => {
    const [warningMessage, setWarningMessage] = useState(false);
    const [selectedTabs, setSelectedTabs] = useState<number>(1); // Assuming there are tabs for navigation
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [appointmentLoading, setIsAppointmentLoading] = useState<boolean>(false);
    const [ticketData, setTicketData] = useState<TicketDto[]>([]);
    const [appointmentNotes, setAppointmentNotes] = useState<NoteDto[]>([]);
    const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
    const [selectedAddress, setSelectedAddress] = useState(
      appointment.address?.contactInfo?.addressLine1 ? formatAddress(appointment.address) : ""
    );

    const [formData, setFormData] = useState<UpdateAppointmentDto>({});
    const [patientAddress, setPatientAddress] = useState<AddressDto[]>([]);
    const [openAddressDialog, setOpenAddressDialog] = useState(false);
    const [appStats, setAppStats] = useState<AppStatsDto[]>([]);

    const TicketsStore = useStore(TicketsStoreContext);
    const AppointmentStore = useStore(AppointmentContext);
    const { mutate: updateAppointment } = useUpdateAppointment();
    const { mutate: deletehardTimeslot } = useDeleteHardTimeslot();
    const { mutate: updateMultipleAppointments } = useUpdateMultipleAppointments();
    const { getFilteredAppointmentsForPatient } = AppointmentStore;
    const UserStore = useStore(UserContext);
    const currentLocation = useCurrentLocation();
    const NotesStore = useStore(NotesContext);
    const PatientStore = useStore(PatientsContext);
    const RxStore = useStore(RxStoreContext);
    const { data: patientQueryData } = usePatient(patientId);
    const { getTherapyRxsForPatient, getAppStats } = RxStore;
    const { getAppointmentNotes, postNotes, fetchNotes, setFetchNotes, updateNoteById } =
      NotesStore;
    const { data: masterTablesData } = useMasterTables();
    const { user } = UserStore;
    const [appointmentsListAddressUpdate, setAppointmentsListAddressUpdate] = useState<string[]>(
      []
    );
    const [selectedTherapyRx, setSelectedTherapyRx] = useState<TherapyRxDto>({});
    const [therapyOptions, setTherapyOptions] = useState<TherapyRxDto[]>([]);
    const [openSelection, setOpenSelection] = useState(false);
    const [openWarningDialog, setOpenwarningDialog] = useState(false);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlotDto | null>(null);
    // MD-302: which slot's therapy-method picker is open (null = closed).
    const [methodPickerSlotId, setMethodPickerSlotId] = useState<string | null>(null);

    const ticketFilters = { limit: 10, appointmentId: appointment.id };
    const {
      data: ticketsData,
      isFetchingNextPage,
      hasNextPage: hasMore,
      fetchNextPage,
    } = useTickets({
      filters: ticketFilters,
      enabled: !!ticketFilters.appointmentId,
    });

    const tickets = ticketsData?.pages.reduce((tickets, page) => tickets.concat(page), []);

    useEffect(() => {
      const fetchAppointments = async () => {
        try {
          setIsAppointmentLoading(true);

          if (
            isView === TerminViewConstants.SERIES_VIEW ||
            isView === TerminViewConstants.RX_VIEW
          ) {
            const response = await getFilteredAppointmentsForPatient(
              patientId,
              {
                showOnlyFutureAppointments: false,
                showOnlyActiveRxs: false,
                showLastThreeMonths: false,
                rxIds: [],
                appointmentId: appointment?.id,
              },
              false
            );

            if (response) setFormData(response[0]);
          } else {
            setFormData(appointment);
          }
        } catch (error) {
          notificationStore.showMessage("Fehler beim Laden der Termine:", "error");
        } finally {
          setIsAppointmentLoading(false);
        }
      };

      fetchAppointments();
    }, []);

    // Effect to check if ALL time slots have "keine" visitFee
    useEffect(() => {
      const allKeine = formData?.timeSlots?.every(
        (timeSlot) =>
          !timeSlot?.visitFee?.abbreviation || timeSlot?.visitFee?.abbreviation === "keine"
      );

      if (allKeine && formData?.address?.type?.toLowerCase() !== "praxis") {
        setWarningMessage(true);
      } else {
        setWarningMessage(false);
      }
    }, [formData?.timeSlots, formData?.address?.type]); // Runs when timeSlots or address type change
    const updateMultipleAppointmentsAddress = async (visitFee: TherapyDetailsDto | null) => {
      try {
        setIsLoading(true);

        // Check if formData.id is in the appointmentsListAddressUpdate, and add it if not
        const appointmentIds = [...appointmentsListAddressUpdate];
        if (formData.id && !appointmentIds.includes(formData.id)) {
          appointmentIds.push(formData.id); // Add the formData.id if it's not already in the list
        }

        // Prepare the data for updating
        const data: UpdateAppointmentDto[] = appointmentIds.map((appointmentId) => {
          // If formData.id exists, return formData directly
          if (formData.id && appointmentId === formData.id) {
            return { ...formData, visitFee: visitFee! }; // Return the formData directly if the IDs match
          }
          // Initialize the appointment object
          const appointment: UpdateAppointmentDto = {
            id: appointmentId,
            address: formData.address,
            timeSlots: [], // Empty timeSlots as per your example, can be modified if needed
          };

          // Only include visitFee if it exists
          if (visitFee !== undefined && visitFee !== null) {
            appointment.visitFee = visitFee;
          }

          return appointment;
        });
        updateMultipleAppointments({
          updateAppointmentDto: data,
          isContinuous: isContinous ? isContinous : false,
          patientId: patientId,
        });

        // Close the dialog after the update
        onClose(true);
      } catch (error) {
        console.error("Error updating appointment:", error);
        // Handle error (e.g., show error message to user)
      } finally {
        setIsLoading(false); // Set loading state to false regardless of success or failure
      }
    };

    // const updateMultipleAppointmentsAddress = async () => {
    //   try {
    //     setIsLoading(true)
    //     const data: UpdateAppointmentDto[] = appointmentsListAddressUpdate.map((appointmentId) => {
    //       return {
    //         id: appointmentId,
    //         address:formData.address,
    //         timeSlots:[],
    //       };
    //     });
    //     // Call your update API here with formData
    //     console.log("Updating appointment with data:", data);
    //     const response = await updateMultipleAppointments( data, isContinous);
    //     if (response) {
    //       setDataRefreshFlag(!dataRefreshFlag);
    //     }
    //     onClose(true);
    //   } catch (error) {
    //     console.error("Error updating appointment:", error);
    //     // Handle error (e.g., show error message to user)
    //   }
    //   finally {
    //     setIsLoading(false); // Set loading state to false regardless of success or failure
    //   }
    // };
    // const [refresh, setRefresh] = useState(false); // Use a flag

    // Wrapper function for setAppointmentNotes to match the required type
    const handleSetNotes = async (index: number, updatedCard: NoteDto) => {
      if (appointment.id) {
        updatedCard.appointmentId = appointment.id;
        if (updatedCard.id) {
          await updateNoteById(updatedCard.id, user?.id as string, updatedCard);
        } else {
          await postNotes(user?.id as string, updatedCard);
        }
        const updatedNotes = [...appointmentNotes];
        updatedNotes[index] = updatedCard;
        setAppointmentNotes(updatedNotes);
        // setFetchNotes(!fetchNotes);
      } else {
        // console.log("No selectedAppointmentId available.");
      }
    };

    // Data for the table
    useEffect(() => {
      const fetchData = async () => {
        if (appointment.id) {
          setIsLoading(true); // Set loading state to true before fetching data
          try {
            // const response = await getTicketsByAppointmentId(appointment.id);
            // setTicketData(response);
            const appointmentNotes = await getAppointmentNotes(appointment.id);
            setAppointmentNotes(appointmentNotes || []);
          } catch (error) {
            console.error("Error fetching tickets:", error);
            // Handle error (e.g., show error message to user)
          } finally {
            setIsLoading(false); // Set loading state to false regardless of success or failure
          }
        }
      };

      fetchData();
    }, [appointment.id, fetchNotes]);
    const getAppStatsData = async (therapyRxs: TherapyRxDto[]) => {
      if (!therapyRxs || therapyRxs.length === 0) return;

      const rxIds = therapyRxs
        .map((therapyRx: TherapyRxDto) => therapyRx.rx?.id)
        .filter((id): id is string => Boolean(id)); // Ensure valid IDs only

      try {
        const data = await getAppStats(rxIds);

        // Map therapyRxs to corresponding stats
        const sortedStats: any[] = therapyRxs?.map((therapyRx) => {
          const stat = data.find((stat: AppStatsDto) => stat.therapyRxId === therapyRx.id);
          return (
            stat || {
              therapyRxId: therapyRx.id!,
              amountDefined: 0,
              amountPrescribed: 0,
              amountScheduled: 0,
            }
          );
        });

        setAppStats(sortedStats);
      } catch (error) {
        console.error("Error fetching app stats data:", error);
      }
    };

    useEffect(() => {
      const fetchAddressOptions = async () => {
        try {
          let patientAddressOptions: any;

          const therapyOptions = await getTherapyRxsForPatient(patientId);
          setTherapyOptions(therapyOptions);
          await getAppStatsData(therapyOptions); // Fetch app stats after setting Rx data
          patientAddressOptions = patientQueryData;
          // Add user's address if it exists
          if (patientAddressOptions && currentLocation?.address?.contactInfo?.addressLine1) {
            patientAddressOptions = {
              ...patientAddressOptions,
              addresses: [
                ...patientAddressOptions.addresses,
                {
                  ...currentLocation.address,
                },
              ],
            };
          }

          setPatientAddress(patientAddressOptions ? patientAddressOptions.addresses : []);
          if (patientAddressOptions && patientAddressOptions.addresses) {
            const hasPraxisVisit = appointment.timeSlots?.some(
              (timeSlot: any) => timeSlot?.therapyRx?.rx?.visitType?.toLowerCase() === "praxis"
            );

            const formattedAddressOptions = patientAddressOptions.addresses
              .filter(
                (address: any) =>
                  !hasPraxisVisit ||
                  (address?.type !== AddressDtoTypeEnum.Haus &&
                    address?.type !== AddressDtoTypeEnum.Heim)
                // (!address.isVisit && !address.isPrimary && !address.isAlternativeBilling)
              )
              .map((address: any) => {
                const { addressLine1 } = address.contactInfo || {};
                if (addressLine1) {
                  return {
                    label: formatAddress(address),
                    value: formatAddress(address),
                    id: address.id,
                    // isVisit: address.isVisit,
                    visitLocation: address.type,
                    icon: address.type ? visitTypeToIcon[address.type.toLowerCase()] : undefined,
                  };
                }
                return undefined;
              })
              .filter(
                (addressOption: any): addressOption is AddressOption => addressOption !== undefined
              );

            setAddressOptions(formattedAddressOptions);
          }
          // if (user && user?.location?.address) {
          //   setAddresses((prevAddresses) => [
          //     ...(prevAddresses ? prevAddresses : []),
          //     user?.location?.address!,
          //   ]);
          // }
        } catch (error) {
          console.error("Error fetching patient address options:", error);
        }
      };

      fetchAddressOptions();
    }, [patientId, user, patientQueryData]);

    // useEffect(() => {
    //   if (patientId) {
    //     getPatient(patientId)
    //       .then((patient) => {
    //         setAddresses((prevAddresses) => [
    //           ...(prevAddresses ? prevAddresses : []),
    //           ...(patient.addresses?.length ? patient.addresses : []),
    //         ]);
    //       })
    //       .catch((err) => {
    //         console.error(err);
    //       });
    //   }
    // }, [patientId]);

    // Set user address in addresses state arrray
    // useEffect(() => {
    //   if (user && user?.location?.address) {
    //     setAddresses((prevAddresses) => [
    //       ...(prevAddresses ? prevAddresses : []),
    //       user?.location?.address!,
    //     ]);
    //   }
    // }, [user]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, id } = e.target;
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: id ? id : value,
      }));
    };

    const handleAddressChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedId: any = event.target.id;
      setSelectedAddress(event.target.value);

      const selectedAddressOption: any = patientAddress.find(
        (address: any) => address.id === selectedId
      );
      if (
        selectedAddressOption?.type !== AddressDtoTypeEnum.Haus &&
        selectedAddressOption?.type !== AddressDtoTypeEnum.Heim
        // !selectedAddressOption?.isVisit &&
        // !selectedAddressOption?.isPrimary &&
        // !selectedAddressOption?.isAlternativeBilling
      ) {
        const updatedTimeSlots = formData.timeSlots?.map((slot: any) => ({
          ...slot,
          visitFee: null,
        }));
        setFormData((prevFormValues: any) => ({
          ...prevFormValues,
          timeSlots: updatedTimeSlots,
        }));
      }
      if (selectedAddressOption) {
        setFormData((prevFormValues: any) => ({
          ...prevFormValues,
          address: selectedAddressOption,
        }));
      }
    };

    const handleTherapyChange = (e: any) => {
      const selectedTherapy = therapyOptions.find((therapyRx) => therapyRx.id === e.target.id);

      if (selectedTherapy) {
        setSelectedTherapyRx(selectedTherapy);
        handleAddTherapyRx(selectedTherapy);
      }
    };

    const handleAddTherapyRx = (therapyRx: TherapyRxDto) => {
      // MD-232: the series hangs on the Rx, and the swapper works in per-class
      // lanes — so the clash to prevent is two slots of the SAME prescribed
      // term within one series, not two slots of the same series. A line
      // prescribed as a Doppelbehandlung (unitsPerDay > 1) may repeat.
      const rxSeries = (therapyRx.rx as any)?.rxSeries as RxSeriesDto | undefined;
      const termId = therapyRx.prescribedTherapyId ?? therapyRx.prescribedTherapy?.id ?? null;
      const clashes =
        rxSeries &&
        (therapyRx.unitsPerDay ?? 1) <= 1 &&
        appointment.timeSlots?.some(
          (slot: any) =>
            slot.therapyRx?.rx?.rxSeries?.id === rxSeries.id &&
            (slot.therapyRx?.prescribedTherapyId ?? slot.therapyRx?.prescribedTherapy?.id ?? null) ===
              termId
        );
      if (clashes) {
        notificationStore.showMessage(
          "Zwei Behandlungen desselben Heilmittels in einem Termin sind nicht möglich: " +
            rxSeries?.label,
          "error"
        );
        return;
      }

      // MD-232: the server resolves the performed therapy from the line's
      // variant pin / class + the assigned therapist's credentials; the
      // client only provides the line. A locally attached estimation object
      // (from the template widget) is used for optimistic duration display.
      //
      // MD-428: in practice this is ALWAYS null — nothing in either repo writes
      // `estimationTherapy`. So the slot below is appended at the end of the
      // last one with a zero-length window, and this dialog cannot do better:
      // whether the added therapy is passive is a property of the resolved line
      // item (WAERME is HL/PA passive or HR active, by therapist), and there is
      // none here. Do NOT reach for `layoutSlots` to fix the order — it reads
      // passivity from the line item too, so it would classify this slot as
      // active and change nothing.
      //
      // The placement is done where the line item exists: physio-api
      // `placeAddedSupplements`, called just before the windows are re-chained
      // in `updateAppointmentData`. An added Warmpackung comes back from the
      // save in front of the main therapy, and the survivor DTO replaces the
      // optimistic order below. Changing the append here would double-shift it.
      const performedTherapy = (therapyRx as any).estimationTherapy ?? null;

      const newTimeSlot: TimeSlotDto = {
        type: formData?.timeSlots?.[0]?.type || "TimeSlot",
        therapyRx,
        therapy: performedTherapy,
        rxId: (therapyRx.rx as any)?.id ?? null,
        start: moment(
          formData.timeSlots?.[formData.timeSlots.length - 1]?.end || formData.start
        ).toISOString(),
        end: moment(formData.timeSlots?.[formData.timeSlots.length - 1]?.end || formData.start)
          .add(performedTherapy?.duration || 0, "minutes")
          .toISOString(),
        user: appointment?.timeSlots?.[0]?.user,
      };
      // console.log("NEWTIMMS", newTimeSlot);

      setFormData((prevFormData) => ({
        ...prevFormData,
        timeSlots: [...(prevFormData.timeSlots || []), newTimeSlot] as TimeSlotDto[],
      }));
    };

    const handleDeleteTimeslot = async (timeSlot: TimeSlotDto | null) => {
      try {
        if (!timeSlot) {
          throw new Error("No time slot provided");
        }

        if (timeSlot.id) {
          // If the timeSlot has an id, call the delete API
          deletehardTimeslot({
            timeSlotId: timeSlot.id,
            isContinuous: isContinous ? isContinous : false,
            patientId: patientId,
          });

          onClose(true); // Close the dialog after successful deletion
        } else {
          // If no id is present, just remove the timeSlot from the appointment
          setFormData((prevFormData: any) => {
            const updatedTimeSlots = prevFormData?.timeSlots?.filter(
              (timeSlotData: any) => timeSlotData.id !== timeSlot.id
            );
            return { ...prevFormData, timeSlots: updatedTimeSlots };
          });
        }

        setOpenwarningDialog(false); // Close the warning dialog after deletion or removal
      } catch (error) {
        // Handle any errors that occurred during the process
        console.error("Error deleting time slot:", error);
        // Optionally, you can show an alert or toast to notify the user of the error
        notificationStore.showMessage(
          "Beim Löschen des Termins ist ein Fehler aufgetreten. Bitte versuche es erneut.",
          "error"
        );
        setOpenwarningDialog(false); // Close the dialog if there's an error
      }
    };

    const handleOpenWarning = (timeSlot: TimeSlotDto) => {
      setSelectedTimeSlot(timeSlot);
      setOpenwarningDialog(true);
    };
    const handleCloseWarning = () => {
      setOpenwarningDialog(false);
    };

    // Handle change in time slot
    const handleTimeSlotChange = (index: number, key: string, value: any) => {
      const updatedTimeSlots = [...(formData.timeSlots as any)];
      updatedTimeSlots[index] = { ...updatedTimeSlots[index], [key]: value };

      // Handle visitFee reset logic when changing time slot making sure only one have visitFee
      if (key === "visitFee" && value !== null) {
        updatedTimeSlots.forEach((slot, i) => {
          if (i !== index && slot.visitFee !== null) {
            updatedTimeSlots[i] = { ...updatedTimeSlots[i], visitFee: null };
          }
        });
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        timeSlots: updatedTimeSlots,
      }));
    };
    const handleDropDownMenuChange = async (
      index: number,
      newVisitFee: string,
      tarifGroup: any
    ) => {
      let newVisitFeeValue: any;
      if (!newVisitFee) {
        handleTimeSlotChange(index, "visitFee", null);
      } else {
        newVisitFeeValue = getTherapyId(masterTablesData || [], tarifGroup, newVisitFee);
        if (
          formData.address?.type !== AddressDtoTypeEnum.Haus &&
          formData.address?.type !== AddressDtoTypeEnum.Heim
          // !formData.address?.isVisit &&
          // !formData.address?.isPrimary &&
          // !formData.address?.isAlternativeBilling
        ) {
          setSelectedAddress("");
          setFormData((prevFormValues: any) => ({
            ...prevFormValues,
            address: null,
          }));
        }
        handleTimeSlotChange(index, "visitFee", {
          id: newVisitFeeValue,
          abbreviation: newVisitFee,
        });
      }
    };

    // Function to handle tab change
    const handleTabChange = (event: any, newValue: number) => {
      setSelectedTabs(newValue); // Update selectedTabs state variable with the new value
    };

    const handleSave = async () => {
      try {
        setIsLoading(true);
        // Call your update API here with formData
        // console.log("Updating appointment with data:", formData);
        updateAppointment({
          appointmentId: formData.id as string,
          updateAppointmentDto: formData,
          isContinuous: isContinous ? isContinous : false,
          patientId: patientId,
        });

        onClose(true);
      } catch (error) {
        console.error("Error updating appointment:", error);
        // Handle error (e.g., show error message to user)
      } finally {
        setIsLoading(false); // Set loading state to false regardless of success or failure
      }
    };

    const handleCancel = () => {
      onClose(!open);
    };

    const visitFeeOptions = [
      { label: "HB2K", value: "HB2K" },
      { label: "HB7", value: "HB7" },
      { label: "HB", value: "HB" },
      { label: "HB2", value: "HB2" },
      { label: "keine", value: "keine" },
    ];

    const handleSwappingTreatment = () => {
      // const updatedTimeSlots = formData.timeSlots?.slice();
      const updatedTimeSlots = formData.timeSlots?.map((slot) => ({ ...slot }));

      const firstTreatment = updatedTimeSlots?.[0];
      const secondTreatment = updatedTimeSlots?.[1];

      if (firstTreatment && secondTreatment) {
        const firstTreatmentDuration = (slotTherapy(firstTreatment) as TherapyExt).duration;
        const secondTreatmentDuration = (slotTherapy(secondTreatment) as TherapyExt).duration;

        const firstTherapyRx = firstTreatment.therapyRx;
        firstTreatment.therapyRx = secondTreatment.therapyRx;
        secondTreatment.therapyRx = firstTherapyRx;

        const firstStart = moment(firstTreatment.start);
        const secondEnd = moment(secondTreatment.end);

        firstTreatment.start = firstStart.toISOString();
        firstTreatment.end = firstStart.add(secondTreatmentDuration, "minutes").toISOString();

        secondTreatment.start = firstTreatment.end;
        secondTreatment.end = secondEnd.toISOString();

        setFormData((prevFormData) => ({
          ...prevFormData,
          timeSlots: updatedTimeSlots,
        }));
      }
    };

    const handlePassiveTreatment = () => {
      const updatedTimeSlots = formData.timeSlots
        ? formData.timeSlots.map((slot) => ({ ...slot }))
        : [];

      if (updatedTimeSlots.length < 2) return;

      // Separate main therapies and heat treatments (supplementary treatments)
      const mainTherapies = updatedTimeSlots.filter(
        (slot) => !(slotTherapy(slot) as TherapyDto)?.isSupplementary
      );

      const supplementaryTreatments = updatedTimeSlots.filter(
        (slot) => (slotTherapy(slot) as TherapyDto)?.isSupplementary
      );

      if (mainTherapies.length === 0 || supplementaryTreatments.length === 0) {
        notificationStore.showMessage("Keine passive Behandlung zum Austauschen.", "warning");
        return;
      }

      // If there are more than one supplementary treatment, log an error and return
      if (supplementaryTreatments.length > 1) {
        notificationStore.showMessage(
          "Pro Termin kann es nur eine passive Behandlung geben.",
          "warning"
        );
        return;
      }

      // Get the earliest and latest times for the main therapies (non-supplementary treatments)
      const mainTherapyStart = moment.min(mainTherapies.map((slot) => moment(slot.start)));
      const mainTherapyEnd = moment.max(mainTherapies.map((slot) => moment(slot.end)));

      // Loop through each supplementary treatment and adjust its time
      supplementaryTreatments.forEach((heatTreatment) => {
        const heatTherapyDuration = (slotTherapy(heatTreatment) as TherapyExt).duration;

        if (moment(heatTreatment.start)?.isBefore(mainTherapyStart)) {
          // If heat treatment is already before main therapy, move it AFTER the last main therapy
          heatTreatment.start = mainTherapyEnd.toISOString();
          heatTreatment.end = moment(heatTreatment.start)
            .add(heatTherapyDuration, "minutes")
            .toISOString();
        } else {
          // Otherwise, move it BEFORE the first main therapy
          heatTreatment.start = mainTherapyStart
            .clone()
            .subtract(heatTherapyDuration, "minutes")
            .toISOString();
          heatTreatment.end = moment(heatTreatment.start)
            .add(heatTherapyDuration, "minutes")
            .toISOString();
        }
      });

      // **Now, update the overall appointment start and end time:**
      // Find the earliest start and latest end from the updated time slots
      const earliestStart = moment.min(updatedTimeSlots.map((slot) => moment(slot.start)));
      const latestEnd = moment.max(updatedTimeSlots.map((slot) => moment(slot.end)));
      // **Now, sort the time slots** by their start time to ensure correct order
      const sortedTimeSlots = [...updatedTimeSlots].sort((a, b) =>
        moment(a.start)?.isBefore(b.start) ? -1 : 1
      );

      // Update state
      setFormData((prevFormData) => ({
        ...prevFormData,
        start: earliestStart.toISOString(), // Total start time (earliest across all)
        end: latestEnd.toISOString(), // Total end time (latest across all)
        timeSlots: sortedTimeSlots, // Updated time slots
      }));
    };

    const handleTimeiiSlotChange = (index: number, key: string, value: any) => {
      const updatedTimeSlots = [...(formData.timeSlots as any)];

      // If removing a supplementary treatment, hard delete the time slot
      if (key === "therapyRx" && value === null) {
        // MD-232: "supplementary" is a property of the prescribed TERM
        // (Wärme, Kälte, Kompression), not of a billable line item — a line
        // carries no therapy any more.
        const isSupplementary =
          updatedTimeSlots[index].therapyRx?.prescribedTherapy?.isSupplementary === true;

        if (isSupplementary) {
          updatedTimeSlots.splice(index, 1); // Hard delete
        } else {
          updatedTimeSlots[index] = { ...updatedTimeSlots[index], [key]: null }; // Soft delete
        }
      } else {
        updatedTimeSlots[index] = { ...updatedTimeSlots[index], [key]: value };
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        timeSlots: updatedTimeSlots,
      }));
    };

    const updateAppointmentTimes = () => {
      const sortedTimeSlots = [...(formData.timeSlots as any)].sort((a, b) =>
        moment(a.start).isBefore(b.start) ? -1 : 1
      );

      const earliestStart = moment.min(sortedTimeSlots.map((slot) => moment(slot.start)));
      const latestEnd = moment.max(sortedTimeSlots.map((slot) => moment(slot.end)));

      setFormData((prevFormData) => ({
        ...prevFormData,
        start: earliestStart.toISOString(),
        end: latestEnd.toISOString(),
        timeSlots: sortedTimeSlots,
      }));
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
        <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
          <DialogTitle>Termin bearbeiten</DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "800px", // Set a fixed height
              marginTop: "4px",
            }}
          >
            {appointmentLoading && (
              <Modal
                open={appointmentLoading}
                aria-labelledby="loading-modal-title"
                aria-describedby="loading-modal-description"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div>
                  <CircularProgress size={60} />
                </div>
              </Modal>
            )}
            {!appointmentLoading && (
              <>
                <FlexListContainer
                  sx={{
                    flex: "0 1 70%", // Allow FlexListContainer to take 70% of the height
                    overflow: " hidden auto", // Enable vertical scrolling
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <FlatCard title="">
                        <Grid container spacing={1}>
                          {/* Start DateTimePicker */}
                          <Grid item xs={12} md={3}>
                            <LabelItem label="Start" />
                            <DateTimePicker
                              label="DD.MM.YYYY"
                              name="start"
                              value={formData.start ? new Date(formData.start as string) : null}
                              onChange={(e: any) => handleInputChange(e)}
                              disabled
                            />
                          </Grid>

                          {/* End DateTimePicker */}
                          <Grid item xs={12} md={3}>
                            <LabelItem label="Ende" />
                            <DateTimePicker
                              label="DD.MM.YYYY"
                              name="end"
                              value={formData.end ? new Date(formData.end) : null}
                              onChange={(e: any) => handleInputChange(e)}
                              disabled
                            />
                          </Grid>

                          {/* Address Select */}
                          <Grid item xs={12} md={6}>
                            <LabelItem label="Addresse" />

                            {/* Flex container for the select and DoneAll icon */}
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <Controls.Select
                                name="address"
                                value={selectedAddress}
                                onChange={handleAddressChange}
                                options={addressOptions}
                                id={1}
                                style={{ width: "100%" }}
                                onBlur={true}
                              />

                              {/* DoneAll icon with onClick toggle for address dialog */}
                              {/* <Tooltip title="Alle Termine aktualisieren">
    <DoneAll
      onClick={() => {
        setOpenAddressDialog(!openAddressDialog);
      }}
      sx={{ marginLeft: "1px", cursor: "pointer",color:mobiliTheme.palette.info.light }} // Added margin and cursor for better layout
    /></Tooltip> */}
                            </div>
                          </Grid>
                        </Grid>
                      </FlatCard>
                    </Grid>
                    <Grid item xs={12}>
                      {formData.timeSlots?.length! > 1 && (
                        <>
                          {/* New MoveDown Button */}
                          {formData.start && (
                            <Tooltip title="Behandlung nach unten verschieben">
                              <Button onClick={handlePassiveTreatment}>
                                <MoveDown />
                              </Button>
                            </Tooltip>
                          )}

                          {/* Existing Swap Button */}
                          <Tooltip title="Behandlungen tauschen">
                            <Button onClick={handleSwappingTreatment}>
                              <SwapHorizIcon />
                            </Button>
                          </Tooltip>
                        </>
                      )}
                      {formData.timeSlots?.length! >= 1 && (
                        <Button
                          onClick={() => setOpenSelection(true)}
                          variant="contained"
                          disabled={!selectedTherapyRx}
                          sx={{ margin: "10px" }}
                        >
                          Hinzufügen
                        </Button>
                      )}
                      {openSelection && (
                        <>
                          <LabelItem label="Therapie hinzufügen" />
                          <Controls.Select
                            name="newTherapyRx"
                            value={selectedTherapyRx.rx?.rxNumber}
                            onChange={(e: any) => handleTherapyChange(e)}
                            id={1}
                            options={therapyOptions.map((therapyRx: any) => {
                              const appsStats = appStats?.find(
                                (stats) => stats.therapyRxId === therapyRx.id
                              );

                              // Determine the values based on appsStats
                              const VO = appsStats
                                ? appsStats.amountPrescribed < 0
                                  ? "∞"
                                  : appsStats.amountPrescribed
                                : "";
                              const PLANNED = appsStats ? appsStats.amountDefined : "";

                              return {
                                value: therapyRx.rx?.rxNumber?.toString(),
                                label: `${therapyRxTermLabel(therapyRx)} [VO: ${VO} PLANNED: ${PLANNED}]`,
                                id: therapyRx.id,
                              };
                            })}
                            style={{ width: "100%" }}
                          />
                        </>
                      )}

                      <Grid container spacing={1}>
                        {formData.timeSlots?.map((timeSlot, index) => (
                          <Grid item xs={12} md={6} key={index}>
                            <FlatCard
                              title={`Behandlung ${index + 1}`}
                              action={
                                (slotTherapy(timeSlot) as TherapyDto)?.isSupplementary ? (
                                  <IconButton
                                    color="error"
                                    onClick={() => handleOpenWarning(timeSlot)}
                                  >
                                    <Delete />
                                  </IconButton>
                                ) : null
                              }
                            >
                              {openWarningDialog && (
                                <WarningDialog
                                  open={true} // Ensure the warning dialog is open
                                  onClose={handleCloseWarning} // Function to close the dialog
                                  onContinue={() => handleDeleteTimeslot(selectedTimeSlot)} // Function to continue despite unsaved changes
                                  title="TimeSlot löschen"
                                  content="Bestätigen Sie, um das ausgewählte Zeitfenster zu löschen."
                                />
                              )}
                              <React.Fragment key={index}>
                                <Grid container spacing={1}>
                                  <Grid item xs={12} sm={6}>
                                    <LabelItem label="Start" />
                                    <DateTimePicker
                                      label="DD.MM.YYYY"
                                      value={timeSlot.start ? new Date(timeSlot.start) : null}
                                      onChange={(value) =>
                                        handleTimeSlotChange(index, "start", value)
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <LabelItem label="End" />
                                    <DateTimePicker
                                      label="DD.MM.YYYY"
                                      value={timeSlot.end ? new Date(timeSlot.end) : null}
                                      onChange={(value) =>
                                        handleTimeSlotChange(index, "end", value)
                                      }
                                      sx={{ height: "calc(100% / 40)" }}
                                    />
                                  </Grid>

                                  <LabelItem label="Pauschale" />
                                  <Grid item xs={12} md={6}>
                                    <Controls.Select
                                      name="visitFee"
                                      value={timeSlot?.visitFee?.abbreviation || "keine"}
                                      onChange={(e: any) => {
                                        const selectedValue =
                                          e.target.value === "keine" ? null : e.target.value;
                                        handleDropDownMenuChange(
                                          index,
                                          selectedValue,
                                          (slotTherapy(timeSlot) as any)?.tarifGroup?.id
                                        );
                                      }}
                                      disabled={formData?.address?.type?.toLowerCase() === "praxis"}
                                      options={
                                        timeSlot.type === "SalesTimeSlot"
                                          ? visitFeeOptions.filter(
                                              (option) =>
                                                option.value === "HB" || option.value === "keine"
                                            )
                                          : visitFeeOptions
                                      }
                                      style={{
                                        width: "100%",
                                        justifyContent: "space-between",
                                        height: "unset",
                                      }}
                                    />
                                    {warningMessage && (
                                      <Grid item xs={12}>
                                        <Typography
                                          sx={{
                                            color: mobiliTheme.palette.warning.dark,
                                            fontSize: "14px",
                                            marginTop: "10px",
                                          }}
                                        >
                                          Keine Pauschale für Hausbesuch/Einrichtung angegeben
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>

                                  <LabelItem label="Rezept" />
                                  <Grid item xs={12} md={6}>
                                    <ValueItem value={timeSlot.therapyRx?.rx?.rxNumber} />
                                  </Grid>

                                  <LabelItem label="Therapeut" />
                                  <Grid item xs={12} md={6}>
                                    <ValueItem
                                      value={`${timeSlot?.user?.firstName || "-"} ${
                                        timeSlot?.user?.lastName || "-"
                                      }`}
                                    />
                                  </Grid>

                                  <LabelItem label="Behandlung" />
                                  <Grid item xs={12} md={6}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <ValueItem
                                        value={`${
                                          (slotTherapy(timeSlot) as any)?.abbreviation || "-"
                                        }`}
                                      />
                                      {/* MD-302: pick a non-default method for this slot */}
                                      {timeSlot.id &&
                                        (timeSlot.type === "Treatment" || timeSlot.therapyRx) && (
                                          <Button
                                            size="small"
                                            variant="text"
                                            sx={{ textTransform: "none", py: 0, minWidth: 0 }}
                                            onClick={() => setMethodPickerSlotId(timeSlot.id!)}
                                          >
                                            Methode ändern…
                                          </Button>
                                        )}
                                    </Box>
                                  </Grid>

                                  <LabelItem label="VisitType" />
                                  <Grid item xs={12} md={6}>
                                    <ValueItem value={`${formData.address?.type || "-"}`} />
                                  </Grid>
                                </Grid>
                              </React.Fragment>
                            </FlatCard>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* MD-302: per-slot therapy method picker */}
                  <TherapyMethodPickerDialog
                    open={!!methodPickerSlotId}
                    timeSlotId={methodPickerSlotId}
                    onClose={() => setMethodPickerSlotId(null)}
                    onApplied={(newTherapy) =>
                      setFormData((prev: any) => ({
                        ...prev,
                        timeSlots: prev.timeSlots?.map((slot: any) =>
                          slot.id === methodPickerSlotId
                            ? { ...slot, therapy: { ...slot.therapy, ...newTherapy } }
                            : slot,
                        ),
                      }))
                    }
                  />
                  <AppointmentAddress
                    open={openAddressDialog}
                    onClose={() => setOpenAddressDialog(false)}
                    patientId={patientId}
                    appointment={formData}
                    setAppointmentsListAddressUpdate={setAppointmentsListAddressUpdate}
                    appointmentsListAddressUpdate={appointmentsListAddressUpdate}
                    updateMultipleAppointmentsAddress={updateMultipleAppointmentsAddress}
                  />
                </FlexListContainer>
                <Card
                  sx={{
                    flex: "0 1 30%", // Allow Card to take 30% of the height
                    overflowY: "auto", // Enable vertical scrolling
                    marginTop: "5px", // Add space between FlexListContainer and Card
                  }}
                >
                  <Tabs
                    TabScrollButtonProps={{ sx: { height: "0px" } }} // This hides the scroll buttons To check any other effective way
                    orientation="horizontal"
                    value={selectedTabs}
                    onChange={handleTabChange}
                    aria-label="sub-tabs"
                    sx={{ paddingLeft: mobiliTheme.topLevelPadding }}
                  >
                    <Tab iconPosition="start" label="Terminhinweise" value={1} />
                    <Tab
                      iconPosition="start"
                      label="Tickets"
                      value={2}
                      sx={{
                        marginRight: "4px",
                      }}
                    />
                    <Tab iconPosition="start" label="Verlauf" value={3} />
                  </Tabs>
                  {(() => {
                    switch (selectedTabs) {
                      case 1:
                        return (
                          <Notes
                            notes={appointmentNotes}
                            setNotes={handleSetNotes}
                            maxVisibleNotes={appointmentNotes.length}
                            noteKind="Terminhinweis"
                          />
                        );
                      case 2:
                        return (
                          <>
                            {isFetchingNextPage && <CircularProgress />}
                            {!isFetchingNextPage && tickets && tickets.length === 0 && (
                              <Typography>Keine Tickets gefunden</Typography>
                            )}
                            {!isFetchingNextPage && tickets && tickets.length > 0 && (
                              <TicketsTable
                                handleRowClick={() => console.log("jaj")}
                                ticketsData={tickets || []}
                                hasMoreData={!!hasMore}
                                fetchMoreData={fetchNextPage}
                                isLoading={isFetchingNextPage}
                              />
                            )}
                          </>
                        );
                      case 3:
                        return <Typography>Verlauf wird hier angezeigt</Typography>; // Render the History component or content here
                      default:
                        return null; // Handle unexpected tab value
                    }
                  })()}
                </Card>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: "flex-end" }}>
            <Button variant="text" onClick={handleCancel} disabled={isLoading}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} color="primary" variant="contained" disabled={isLoading}>
              Speichern
            </Button>
            <Button
              onClick={() => setOpenAddressDialog(!openAddressDialog)}
              color="primary"
              variant="contained"
              disabled={isLoading}
            >
              Mehrere Termine aktualisieren
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    );
  }
);

export default AppointmentEditCard;
